import _ from 'lodash';
import fs from 'fs-extra';
import path from 'path';
import { log, Session, idsort } from '@swiper/core';
import { now } from '../utils';
import Promise from 'bluebird';
import { Service } from './Service';
import { reader } from '../utils/ndjson';
import stringify from 'json-stringify-safe';

export class SessionStore {

  constructor( opts={} ) {
    _.assign( this, opts );
    if ( ! this.dir ) throw new Error( `SessionStore requires directory` );
    if ( ! this.id ) throw new Error( `SessionStore required id` );
  }

  async read( fn, opts={} ) {
    const file = await this.findFile();
    const exists = await fs.exists( file );
    if ( ! exists ) throw new Error( `Session file ${file} does not exist` );
    const res = [];
    if ( ! fn ) fn = Array.prototype.push.bind( res );
    await reader( file, fn, opts );
    if ( res.length ) return res;
    return;
  }

  async create() {
    const status = await this.getStatus();
    if ( status ) throw new Error( `Cannot create existing session` );
    return fs.ensureFile( this.activepath );
  }

  async write( data ) {
    if ( ! await this.isActive() ) {
      throw new Error( `Cannot write to inactive session ${this.id}` );
    }
    // Re-order object so that the time property is at the front, to
    // make it easier to re-integrate logs from different systems by
    // just sorting them..
    const { ts, ...rest } = _.omit( data, 'sessionId' );
    data = { ts : ts || now(), ...rest };

    const stream = fs.createWriteStream( this.activepath, {
      flags     : 'a',
      encoding  : 'utf8',
    } );
    return stream.write( stringify( data ) + '\n' );
  }

  async findFile() {
    if ( await this.isActive() ) return this.activepath;
    if ( await this.isClosed() ) return this.closedpath;
  }

  async getStatus() {
    if ( await this.isActive() ) return 'active';
    if ( await this.isClosed() ) return 'closed';
  }

  pathto( type ) {
    return path.resolve( this.dir, type, this.id + '.ndjson' );
  }

  get activepath() { return this.pathto( 'active' ); }
  get closedpath() { return this.pathto( 'closed' ); }

  async isActive() { return fs.exists( this.activepath ); }
  async isClosed() { return fs.exists( this.closedpath ); }

  async loadSession( opts={} ) {
    _.defaults( opts, {
      events    : false,
      strict    : false,
    } );
    const session = new Session( { id : this.id } );

    if ( opts.events ) session.events = [];

    const members = {};
    const friends = {};
    const guests = {};

    try {
      await this.read( ev => {
        if ( opts.events ) session.events.push( ev );
        switch ( ev.event ) {
          case 'member-in':   members[ ev.memberId ] = true;  break;
          case 'member-out':  members[ ev.memberId ] = false; break;
          case 'family-in':   members[ ev.memberId ] = true;  break;
          case 'family-out':  members[ ev.memberId ] = false; break;
          case 'friend-in':   friends[ ev.friendId ] = true;  break;
          case 'friend-out':  friends[ ev.friendId ] = false; break;
          case 'guest-in':    guests[ ev.guestId ] = true;    break;
          case 'guest-out':   guests[ ev.guestId ] = false;   break;
          default:
            if ( opts.strict ) {
              throw new Service.Errors.Unavailable(
                `Unknown event type "${ev.event}"`,
              );
            }
            break;
        }
      } );
    } catch( err ) {
      log.error( 'ERROR READING SESSION:', err );
      if ( opts.strict ) {
        throw new Service.Errors.Unavailable(
          `Unable to read session ${this.id}`,
        );
      }
    }

    const svc = this.app.service.bind( this.app );
    const { membershipId } = session;

    // Fill in the membership information
    session.membership = await svc( 'memberships' ).get( membershipId );

    // Make sure all of the family members are in the list
    _.each( session.membership.members, id => {
      if ( _.isNil( id ) ) return;
      if ( ! members[ id ] ) members[ id ] = false;
    } );

    log.debug( 'MEMBERSHIP', session.membership );
    log.debug( 'SESSION MEMBERS', session.membership.members );
    log.debug( 'MEMBERS', members );

    const loader = ( service, presence ) => {
      log.debug( 'LOAD', service, presence );
      const ids = _.keys( presence ).sort( idsort );
      return Promise.map( ids, id => {
        if ( ! id ) return;
        return svc( service ).get( id ).then( person => {
          person.present = presence[ id ];
          log.debug( 'LOADED PERSON', person );
          return person;
        } ).catch( err => {
          log.error( `Could not load ${id}: ${err}` );
          return;
        } );
      } ).then( _.compact );
    };
    // Fill in the people records
    session.members = await loader( 'members', members );
    // Yes, the first argument to loader should be 'members' because
    // it's the service we're using to retrieve them, not their
    // relationship to the session.
    session.friends = await loader( 'members', friends );
    session.guests = await loader( 'guests', guests );

    return session;
  }

}
